import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import { MapPin } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import {
  locationsQueryOptions,
  useUpdateLocationMutation,
} from '@/lib/query-options';
import { formatLocationName } from '@/lib/utils';
import type { BusinessLocationRead, Location } from '@/lib/types';

interface ResolveOrphanDialogProps {
  open: boolean;
  businessId: number;
  row: BusinessLocationRead | null;
  onClose: () => void;
}

const formatReach = (n: number | null | undefined) =>
  n == null
    ? '-'
    : new Intl.NumberFormat(undefined, {
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(n);

export function ResolveOrphanDialog({
  open,
  businessId,
  row,
  onClose,
}: ResolveOrphanDialogProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, 400);
  const search = useQuery(locationsQueryOptions(debouncedQuery));
  const update = useUpdateLocationMutation(businessId);

  // Pre-fill the search with the row's country_code or row label hint.
  useEffect(() => {
    if (open && row?.country_code) {
      setQuery(row.country_code);
    } else if (!open) {
      setQuery('');
    }
  }, [open, row?.country_code]);

  // Filter to admin-level results (the orphan case is admin/country with no
  // location_id; for country we suggest country results).
  const targetTypes =
    row?.variant === 'country' ? ['country'] : ['region', 'state', 'city'];
  const results = (search.data ?? []).filter((loc: Location) =>
    targetTypes.includes(loc.target_type?.toLowerCase() ?? ''),
  );

  const handleSelect = async (loc: Location) => {
    if (!row) return;
    await update.mutateAsync({
      uuid: row.uuid,
      patch: {
        location_id: loc.criteria_id,
        country_code: loc.country_code?.toUpperCase() ?? row.country_code,
      },
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[500px]" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Resolve location</DialogTitle>
        </DialogHeader>
        <Command className="rounded-lg border">
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={`Search ${row?.variant === 'country' ? 'countries' : 'regions'}...`}
          />
          {search.isLoading && query.length > 0 && (
            <div className="p-4 text-sm text-gray-500">Loading...</div>
          )}
          {!search.isLoading && query.length > 0 && (
            <>
              <CommandEmpty>No matches.</CommandEmpty>
              <CommandGroup className="max-h-[300px] overflow-y-auto">
                {results.map((loc) => (
                  <CommandItem
                    key={loc.canonical_name}
                    value={loc.canonical_name}
                    onSelect={() => handleSelect(loc)}
                    className="flex cursor-pointer justify-between px-4 py-3"
                  >
                    <span className="flex items-center gap-2 text-gray-900">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      {formatLocationName(loc.canonical_name)}
                    </span>
                    <span className="text-sm text-gray-500">
                      {formatReach(loc.reach)}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}
        </Command>
        <div className="flex justify-end pt-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={update.isPending}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
